import { useEffect } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import {
  Target, ArrowLeft, ArrowRight, Building2, CheckCircle2, Info,
  Clock, Sliders, AlertCircle, AlertTriangle,
} from "lucide-react";
import type { RadarConfig, RadarCriterion } from "@shared/schema";

interface RadarDetail {
  config: RadarConfig & {
    company?: { id: string; name: string; slug?: string | null; website?: string | null; description?: string | null } | null;
    supplierLeadPerson?: { id: string; name: string; jobTitle?: string | null; email?: string | null } | null;
  };
  criteria: RadarCriterion[];
}

// Generates the JSON-LD graph client-side (pure — no server deps needed)
function buildRadarJsonLd(config: RadarDetail["config"], criteria: RadarCriterion[]): object {
  const base = window.location.origin;
  const pageUrl = `${base}/radar/${config.slug}`;

  const faqItems = criteria
    .filter(c => c.description || c.psaProblemText || c.psaSolutionText || c.psaApplicationText)
    .map(c => {
      const parts: string[] = [];
      if (c.description) parts.push(c.description);
      if (c.psaProblemText) parts.push(`Challenge: ${c.psaProblemText}`);
      if (c.psaSolutionText) parts.push(`Solution: ${c.psaSolutionText}`);
      if (c.psaApplicationText) parts.push(`Application: ${c.psaApplicationText}`);
      return {
        "@type": "Question",
        name: `How does ${config.productName} address ${c.name}?`,
        acceptedAnswer: { "@type": "Answer", text: parts.join(" ") },
      };
    });

  const product: Record<string, unknown> = {
    "@type": "Product",
    name: config.productName,
    url: pageUrl,
  };
  if (config.productDescription) product.description = config.productDescription;
  if (config.productCategory) product.category = config.productCategory;

  const webPage: Record<string, unknown> = {
    "@type": "WebPage",
    "@id": pageUrl,
    name: config.title,
    url: pageUrl,
  };
  if (config.productDescription) webPage.description = config.productDescription;

  const graph: object[] = [webPage, product];
  if (faqItems.length > 0) {
    graph.push({ "@type": "FAQPage", mainEntity: faqItems });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

const INPUT_TYPE_LABELS: Record<string, string> = {
  slider: "Slider",
  toggle: "Yes / No",
  radio: "Single choice",
  multi_select: "Multi-select",
  number_input: "Number",
  range: "Range",
};

export default function RadarLandingPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: radarData, isLoading } = useQuery<RadarDetail>({
    queryKey: [`/api/public/radars/${slug}`],
    enabled: !!slug,
  });

  // Inject JSON-LD structured data into <head>
  useEffect(() => {
    if (!radarData) return;
    const { config, criteria } = radarData;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = `radar-jsonld-${config.slug}`;
    script.textContent = JSON.stringify(buildRadarJsonLd(config, criteria));
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById(`radar-jsonld-${config.slug}`);
      if (existing) existing.remove();
    };
  }, [radarData]);

  // Set document title + meta description
  useEffect(() => {
    if (!radarData) return;
    const { config } = radarData;
    const prevTitle = document.title;
    document.title = `${config.title} | Application Fit Evaluation`;

    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevContent = metaDesc?.content ?? "";
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = config.productDescription
      ? config.productDescription.slice(0, 160)
      : `Evaluate whether ${config.productName} matches your application requirements.`;

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.content = prevContent;
    };
  }, [radarData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 max-w-3xl mx-auto px-4 py-12 w-full space-y-6">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-12 w-40 rounded-lg" />
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!radarData) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h1 className="text-xl font-bold mb-2">Radar Not Found</h1>
          <p className="text-muted-foreground mb-6">This evaluation may no longer be available.</p>
          <Button asChild variant="outline">
            <Link href="/radar"><ArrowLeft className="mr-2 h-4 w-4" />All Radars</Link>
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const { config, criteria } = radarData;
  const estimatedMinutes = Math.max(2, Math.round(criteria.length * 0.5));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />

      <main className="flex-1 max-w-3xl mx-auto px-4 py-12 w-full">
        <Link href="/radar" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" />
          All Radars
        </Link>

        {/* Category + label */}
        <div className="flex items-center gap-2 mb-3">
          <Target className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold text-primary uppercase tracking-wider">Radar Evaluation</span>
          {config.productCategory && (
            <Badge variant="secondary" className="text-xs">{config.productCategory}</Badge>
          )}
        </div>

        <h1 className="text-3xl font-bold mb-2" data-testid="heading-radar-title">{config.title}</h1>
        <p className="text-xl text-muted-foreground mb-4">{config.productName}</p>

        {/* Company */}
        {config.company && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Building2 className="h-4 w-4 shrink-0" />
            {config.company.slug ? (
              <Link href={`/companies/${config.company.slug}`} className="hover:text-foreground hover:underline font-medium text-foreground">
                {config.company.name}
              </Link>
            ) : (
              <strong className="text-foreground">{config.company.name}</strong>
            )}
            {config.company.website && (
              <a href={config.company.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline ml-1">
                Visit website →
              </a>
            )}
          </div>
        )}

        {/* Description */}
        {config.productDescription && (
          <Card className="mb-6">
            <CardContent className="pt-5 pb-5">
              <p className="text-muted-foreground leading-relaxed">{config.productDescription}</p>
            </CardContent>
          </Card>
        )}

        {/* Criteria — expanded with description text for E-E-A-T */}
        {criteria.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
              <Sliders className="h-4 w-4 text-primary" />
              {criteria.length} Evaluation {criteria.length === 1 ? "Criterion" : "Criteria"}
            </h2>
            <div className="space-y-2">
              {criteria.map(c => (
                <div
                  key={c.id}
                  className="rounded-lg border bg-card px-4 py-3"
                  data-testid={`criterion-card-${c.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                        {c.isCritical && (
                          <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
                        )}
                        <span className="font-medium text-sm">{c.name}</span>
                        {c.isCritical && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Critical</Badge>
                        )}
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                          {INPUT_TYPE_LABELS[c.inputType ?? "slider"] ?? c.inputType}
                        </Badge>
                      </div>
                      {c.description && (
                        <p className="text-xs text-muted-foreground leading-relaxed mt-1">{c.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How it works */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardContent className="pt-5 pb-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              How it works
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="font-semibold text-foreground shrink-0">1.</span>
                Rate each criterion one at a time and set how important it is to you
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-foreground shrink-0">2.</span>
                Watch a live radar chart build as you progress through the steps
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-foreground shrink-0">3.</span>
                Get a weighted match score and personalised gap analysis at the end
              </li>
            </ul>
            <div className="mt-4 pt-3 border-t border-primary/20 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> {criteria.length} criteria</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> ~{estimatedMinutes} minutes</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> No sign-in required</span>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button size="lg" asChild className="sm:w-auto" data-testid="button-start-evaluation">
            <Link href={`/radar/${slug}/evaluate`}>
              Start Evaluation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link href="/radar">
              <ArrowLeft className="mr-2 h-4 w-4" />
              All Radars
            </Link>
          </Button>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
